export type AdminRole =
  | "katana_admin"
  | "pharmacy_chain_admin"
  | "pharmacy_admin"
  | "pharmacist";

export interface Membership {
  uid: string;
  role: AdminRole;
  status: "active" | "revoked";
  chainId?: string;
  pharmacyId?: string;
  displayName?: string;
  email?: string;
  membershipId?: string;
}

export interface Chain {
  id: string;
  displayName: string;
  country: string;
  status: string;
  createdAt?: Date | null;
}

export interface Pharmacy {
  id: string;
  chainId: string;
  displayName: string;
  address: string;
  eircode: string;
  psiNumber: string;
  status: string;
}

export interface ChainJoinRequest {
  id: string;
  status: "pending" | "approved" | "declined";
  chainDisplayName: string;
  country: string;
  requesterUid: string;
  requesterEmail: string;
  requesterFullName: string;
  requesterPhone: string;
  website?: string;
  hqAddress?: string;
  expectedPharmacyCount?: number | null;
  notes?: string;
  createdAt?: Date | null;
  decidedByUid?: string;
  decidedAt?: Date | null;
  declineReason?: string;
  approvedChainId?: string;
}

export interface AuditEvent {
  id: string;
  chainId: string;
  pharmacyId: string;
  actorUid: string;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  targetLabel: string;
  timestamp: Date | null;
  meta?: Record<string, unknown>;
}

export interface AuditActionOption {
  value: string;
  label: string;
}

export const AUDIT_ACTION_OPTIONS: AuditActionOption[] = [
  { value: "", label: "All actions" },
  { value: "login_success", label: "Sign-in success" },
  { value: "login_failure", label: "Sign-in failure" },
  { value: "role_change", label: "Role change" },
  { value: "data_export", label: "Patient data export" },
  { value: "data_delete", label: "Patient data delete" },
  { value: "consent_change", label: "Consent change" },
  { value: "membership_activated", label: "Membership activated" },
  { value: "membership_revoked", label: "Membership revoked" },
  { value: "membership_suspended", label: "Membership suspended" },
  { value: "membership_reactivated", label: "Membership reactivated" },
  { value: "chain_created", label: "Chain created" },
  { value: "chain_edited", label: "Chain edited" },
  { value: "pharmacy_created", label: "Pharmacy created" },
  { value: "pharmacy_edited", label: "Pharmacy edited" },
  { value: "pharmacist_invited", label: "Pharmacist invited" },
  { value: "chain_admin_invited", label: "Chain admin invited" },
  { value: "invite_revoked", label: "Invite revoked" },
  { value: "invite_resent", label: "Invite resent" },
  { value: "chain_join_request_submitted", label: "Join request submitted" },
  { value: "chain_join_request_approved", label: "Join request approved" },
  { value: "chain_join_request_declined", label: "Join request declined" },
  { value: "katana_admin_bootstrapped", label: "Katana admin bootstrapped" },
  { value: "consultation_created", label: "Consultation created" },
  { value: "consultation_updated", label: "Consultation updated" },
  { value: "consultation_deleted", label: "Consultation deleted" },
  { value: "consultation_viewed", label: "Patient record opened (server-verified)" },
  { value: "patient_record_opened", label: "Patient record opened (client-reported)" },
  { value: "patient_record_emailed", label: "Patient record emailed" },
  { value: "patient_record_exported_pdf", label: "Patient record exported (PDF)" },
  { value: "patient_record_printed", label: "Patient record printed" },
  { value: "image_uploaded", label: "Image uploaded" },
  { value: "image_analyzed", label: "Image analyzed" },
];

export interface InviteResult {
  inviteId: string;
  token: string;
  link: string;
  email: string;
  expiresAt: string;
}

export function membershipRoleLabel(
  role: string,
  chainId?: string,
  pharmacyId?: string,
): string {
  switch (role) {
    case "katana_admin":
      return "Katana Admin";
    case "pharmacy_chain_admin":
      return chainId ? `Chain Admin · ${chainId}` : "Chain Admin";
    case "pharmacy_admin":
      return pharmacyId ? `Pharmacy Admin · ${pharmacyId}` : "Pharmacy Admin";
    case "pharmacist":
      return pharmacyId ? `Pharmacist · ${pharmacyId}` : "Pharmacist";
    default:
      return role;
  }
}
